import React, { useState } from 'react';
import EditableBlock, { EditableBlockFooter } from './EditableBlock';
import { formatPhoneFR } from '../../utils/formatFR';
import PhoneInput from '../shared/PhoneInput';
import sortSelectOptions from '../../utils/sortSelectOptions';
import { TYPE_CONTACT_OPTIONS, FORME_JURIDIQUE_OPTIONS, optionRole, libelleRole } from '../../constants/contactEnums';

const SubSectionTitle = ({ icon, label }) => (
  <p className="text-[11px] font-bold uppercase tracking-wider text-blue-700 mb-3 pb-1.5 border-b border-blue-100">
    <i className={`bi ${icon} mr-1`}></i>
    {label}
  </p>
);

const ReadField = ({ label, value, mono = false }) => {
  if (!value && value !== 0) return null;
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <p className={`font-medium text-slate-900 ${mono ? 'num' : ''}`}>{value}</p>
    </div>
  );
};

const InputField = ({ label, value, onChange, type = 'text', placeholder, testid }) => (
  <div>
    <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
      {label}
    </label>
    <input
      type={type}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testid}
      className="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400 outline-none"
    />
  </div>
);

/**
 * Bloc fusionné Identité + Coordonnées + Société.
 * 1 seul bloc, 1 seul crayon, 3 sous-sections internes (cf. mockup v4).
 */
const IdentityContactBlock = ({ contact, onSave }) => {
  const buildInitialForm = (c) => ({
    titre: c?.titre || '',
    prenom: c?.prenom || '',
    nom: c?.nom || '',
    type_contact: c?.type_contact || '',
    // Téléphones / Emails : on garde le tableau complet mais on édite uniquement le principal
    telephones: c?.telephones || [],
    emails: c?.emails || [],
    adresse: c?.adresse || '',
    code_postal: c?.code_postal || '',
    ville: c?.ville || '',
    pays: c?.pays || 'France',
    nom_societe: c?.nom_societe || '',
    profession: c?.profession || '',
    forme_juridique: c?.forme_juridique || '',
    siren_rcs: c?.siren_rcs || '',
    siege_social: c?.siege_social || '',
    representant: c?.representant || '',
    telephone_pro: c?.telephone_pro || '',
    email_pro: c?.email_pro || '',
  });

  const [form, setForm] = useState(() => buildInitialForm(contact));

  // Sync si le contact change depuis l'extérieur
  React.useEffect(() => {
    setForm(buildInitialForm(contact));
  }, [contact?.id]);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const updateMainPhone = (numero) => {
    const phones = [...(form.telephones || [])];
    const mainIdx = phones.findIndex((p) => p.principal);
    if (mainIdx >= 0) {
      phones[mainIdx] = { ...phones[mainIdx], numero };
    } else if (phones.length > 0) {
      phones[0] = { ...phones[0], numero };
    } else {
      phones.push({ numero, type: 'Mobile', principal: true });
    }
    setForm((f) => ({ ...f, telephones: phones }));
  };

  const updateMainEmail = (email) => {
    const emails = [...(form.emails || [])];
    const mainIdx = emails.findIndex((e) => e.principal);
    if (mainIdx >= 0) {
      emails[mainIdx] = { ...emails[mainIdx], email };
    } else if (emails.length > 0) {
      emails[0] = { ...emails[0], email };
    } else {
      emails.push({ email, type: 'Principal', principal: true });
    }
    setForm((f) => ({ ...f, emails }));
  };

  const getMainPhone = (c) => {
    const phones = c?.telephones || [];
    const main = phones.find((p) => p.principal) || phones[0];
    return main?.numero || null;
  };
  const getMainEmail = (c) => {
    const emails = c?.emails || [];
    const main = emails.find((e) => e.principal) || emails[0];
    return main?.email || null;
  };

  const handleSave = async (save) => {
    await save({
      titre: form.titre || null,
      prenom: form.prenom || null,
      nom: form.nom,
      type_contact: form.type_contact,
      telephones: form.telephones,
      emails: form.emails,
      adresse: form.adresse || null,
      code_postal: form.code_postal || null,
      ville: form.ville || null,
      pays: form.pays || 'France',
      nom_societe: form.nom_societe || null,
      profession: form.profession || null,
      forme_juridique: form.forme_juridique || null,
      siren_rcs: form.siren_rcs || null,
      siege_social: form.siege_social || null,
      representant: form.representant || null,
      telephone_pro: form.telephone_pro || null,
      email_pro: form.email_pro || null,
    });
  };

  return (
    <EditableBlock
      title="Identité & contact"
      icon="bi-person-vcard"
      testid="contact-identity-block"
      onSave={onSave}
      renderView={() => (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Identité */}
          <div>
            <SubSectionTitle icon="bi-person" label="Identité" />
            <div className="space-y-2.5 text-sm">
              <ReadField
                label="Civilité · Prénom · Nom"
                value={[contact?.titre, contact?.prenom, contact?.nom].filter(Boolean).join(' ')}
              />
              {contact?.type_contact && (
                // lot DE n°1 — libellé affiché, valeur stockée inchangée (`vendeur` → « Vendeur »)
                <ReadField label="Type de contact" value={libelleRole(contact.type_contact) || contact.type_contact} />
              )}
              {contact?.statut_lead && (
                <ReadField label="Statut" value={contact.statut_lead} />
              )}
            </div>
          </div>

          {/* Coordonnées */}
          <div>
            <SubSectionTitle icon="bi-telephone" label="Coordonnées" />
            <div className="space-y-2.5 text-sm">
              {getMainPhone(contact) && (
                <ReadField label="Téléphone" value={formatPhoneFR(getMainPhone(contact))} mono />
              )}
              {getMainEmail(contact) && (
                <ReadField label="Email" value={getMainEmail(contact)} />
              )}
              {(contact?.adresse || contact?.ville) && (
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                    Adresse
                  </p>
                  {contact?.adresse && <p className="font-medium text-slate-900">{contact.adresse}</p>}
                  <p className="text-slate-700 num text-xs">
                    {[contact?.code_postal, contact?.ville, contact?.pays].filter(Boolean).join(' ')}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Société */}
          <div>
            <SubSectionTitle icon="bi-briefcase" label="Société" />
            <div className="space-y-2.5 text-sm">
              {(contact?.nom_societe || contact?.forme_juridique || contact?.siren_rcs || contact?.representant) ? (
                <>
                  {contact?.forme_juridique && <ReadField label="Forme juridique" value={contact.forme_juridique} />}
                  {contact?.nom_societe && <ReadField label="Raison sociale" value={contact.nom_societe} />}
                  {contact?.siren_rcs && <ReadField label="SIREN / RCS" value={contact.siren_rcs} mono />}
                  {contact?.siege_social && <ReadField label="Siège social" value={contact.siege_social} />}
                  {contact?.representant && <ReadField label="Représentant" value={contact.representant} />}
                  {contact?.profession && <ReadField label="Fonction" value={contact.profession} />}
                  {contact?.telephone_pro && <ReadField label="Téléphone pro" value={formatPhoneFR(contact.telephone_pro)} mono />}
                  {contact?.email_pro && <ReadField label="Email pro" value={contact.email_pro} />}
                </>
              ) : (
                <p className="text-xs text-slate-400 italic">Aucune information professionnelle</p>
              )}
            </div>
          </div>
        </div>
      )}
      renderEdit={({ closeEdit, save, saving }) => (
        <div className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {/* Identité - Edit */}
            <div>
              <SubSectionTitle icon="bi-person" label="Identité" />
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-2">
                  <div className="col-span-1">
                    <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
                      Civilité
                    </label>
                    <select
                      value={form.titre || ''}
                      onChange={(e) => update('titre', e.target.value)}
                      data-testid="identity-titre"
                      className="w-full px-2 py-1.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/40 outline-none"
                    >
                      <option value="">—</option>
                      <option value="M.">M.</option>
                      <option value="Mme">Mme</option>
                      <option value="Dr">Dr</option>
                    </select>
                  </div>
                  <div className="col-span-2">
                    <InputField
                      label="Prénom"
                      value={form.prenom}
                      onChange={(v) => update('prenom', v)}
                      testid="identity-prenom"
                    />
                  </div>
                </div>
                <InputField
                  label="Nom"
                  value={form.nom}
                  onChange={(v) => update('nom', v)}
                  testid="identity-nom"
                />
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
                    Type de contact
                  </label>
                  <select
                    value={optionRole(form.type_contact)?.value || ''}
                    onChange={(e) => update('type_contact', e.target.value)}
                    data-testid="identity-type-contact"
                    className="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/40 outline-none capitalize"
                  >
                    <option value="">—</option>
                    {sortSelectOptions(TYPE_CONTACT_OPTIONS).map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Coordonnées - Edit */}
            <div>
              <SubSectionTitle icon="bi-telephone" label="Coordonnées" />
              <div className="space-y-3">
                <InputField
                  label="Email principal"
                  value={getMainEmail(form) || ''}
                  onChange={updateMainEmail}
                  type="email"
                  placeholder="email@exemple.com"
                  testid="identity-email"
                />
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
                    Téléphone principal
                  </label>
                  <PhoneInput
                    value={getMainPhone(form) || ''}
                    onChange={({ e164, national }) => updateMainPhone(e164 || national || '')}
                    testid="identity-phone"
                  />
                </div>
                <InputField
                  label="Adresse"
                  value={form.adresse}
                  onChange={(v) => update('adresse', v)}
                  testid="identity-adresse"
                />
                <div className="grid grid-cols-3 gap-2">
                  <div className="col-span-1">
                    <InputField
                      label="CP"
                      value={form.code_postal}
                      onChange={(v) => update('code_postal', v)}
                      testid="identity-cp"
                    />
                  </div>
                  <div className="col-span-2">
                    <InputField
                      label="Ville"
                      value={form.ville}
                      onChange={(v) => update('ville', v)}
                      testid="identity-ville"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Société - Edit */}
            <div>
              <SubSectionTitle icon="bi-briefcase" label="Société" />
              <div className="space-y-3">
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
                    Forme juridique
                  </label>
                  <select
                    value={form.forme_juridique || ''}
                    onChange={(e) => update('forme_juridique', e.target.value)}
                    data-testid="identity-forme-juridique"
                    className="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400 outline-none"
                  >
                    <option value="">—</option>
                    {sortSelectOptions(FORME_JURIDIQUE_OPTIONS).map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <InputField
                  label="Raison sociale"
                  value={form.nom_societe}
                  onChange={(v) => update('nom_societe', v)}
                  testid="identity-societe"
                />
                <InputField
                  label="SIREN / RCS"
                  value={form.siren_rcs}
                  onChange={(v) => update('siren_rcs', v)}
                  testid="identity-siren-rcs"
                  placeholder="123 456 789"
                />
                <InputField
                  label="Siège social"
                  value={form.siege_social}
                  onChange={(v) => update('siege_social', v)}
                  testid="identity-siege-social"
                  placeholder="12 rue de la Paix, 75002 Paris"
                />
                <InputField
                  label="Représentant"
                  value={form.representant}
                  onChange={(v) => update('representant', v)}
                  testid="identity-representant"
                  placeholder="Nom du gérant / dirigeant"
                />
                <InputField
                  label="Fonction"
                  value={form.profession}
                  onChange={(v) => update('profession', v)}
                  testid="identity-fonction"
                />
                <InputField
                  label="Téléphone pro"
                  value={form.telephone_pro}
                  onChange={(v) => update('telephone_pro', v)}
                  testid="identity-telephone-pro"
                  placeholder="+33 1 23 45 67 89"
                />
                <InputField
                  label="Email pro"
                  value={form.email_pro}
                  onChange={(v) => update('email_pro', v)}
                  type="email"
                  testid="identity-email-pro"
                  placeholder="contact@societe.fr"
                />
              </div>
            </div>
          </div>

          <EditableBlockFooter
            onCancel={closeEdit}
            onSave={() => handleSave(save)}
            saving={saving}
            testid="identity-block"
          />
        </div>
      )}
    />
  );
};

export default IdentityContactBlock;
